<template>
    <div class="plan-lock-wrapper" :class="[`is-${resolvedVariant}`, { 'is-locked': locked }]">
        <!-- Existing card layout stays intact underneath -->
        <div class="plan-lock-content" :aria-hidden="locked ? 'true' : null" :inert="locked || null">
            <slot />
        </div>

        <!-- Non-destructive visual layer shown only when the item is locked by the plan -->
        <div v-if="locked" class="plan-lock-layer" role="note" :aria-label="resolvedMessage" :title="resolvedMessage">
            <i class="pi pi-lock plan-lock-icon" aria-hidden="true"></i>
            <p class="plan-lock-message">{{ resolvedMessage }}</p>
            <slot name="action">
                <span class="plan-lock-hint">{{ hint }}</span>
            </slot>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import { useAuthStore } from '@/stores/authStore';

/**
 * Generic "locked by subscription plan" overlay.
 *
 * Mirrors SuspendedMemberOverlay (institute capacity suspension) so locked content reads the same
 * way everywhere. Nothing is deleted — the card stays visible but greyed and non-interactive, and
 * unlocks by itself once the plan is upgraded.
 *
 * Backend supplies `lockedByPlan` per item; see utils/planLock.js.
 */
const props = defineProps({
    locked: {
        type: Boolean,
        default: false
    },
    // What is being locked, used to build the default message e.g. "note", "notice", "tutor"
    itemType: {
        type: String,
        default: 'item'
    },
    // Overrides the generated message entirely
    message: {
        type: String,
        default: ''
    },
    hint: {
        type: String,
        default: 'Upgrade plan to restore access'
    },
    // Accent used by the hint chip. Defaults to the signed-in user's role so the lock
    // reads in the same colour the rest of that dashboard uses (teal / orange / yellow).
    variant: {
        type: String,
        default: '',
        validator: (value) => ['', 'tutor', 'student', 'institute'].includes(value)
    }
});

const VARIANTS = ['tutor', 'student', 'institute'];

const authStore = useAuthStore();

const resolvedVariant = computed(() => {
    if (VARIANTS.includes(props.variant)) return props.variant;
    const role = authStore.userType || authStore.user?.role;
    return VARIANTS.includes(role) ? role : 'institute';
});

const resolvedMessage = computed(
    () => props.message
        || `Locked — this ${props.itemType} exceeds your current plan's limit. `
        + `Upgrade your plan to restore access.`
);
</script>

<style scoped>
.plan-lock-wrapper {
    position: relative;
    height: 100%;

    /* institute accent (yellow) — matches SuspendedMemberOverlay */
    --plan-lock-hint-bg: #ffd54a;
    --plan-lock-hint-fg: #5a3d00;
}

/* tutor accent (green) */
.plan-lock-wrapper.is-tutor {
    --plan-lock-hint-bg: #4ddbc4;
    --plan-lock-hint-fg: #00413a;
}

/* student accent (red) */
.plan-lock-wrapper.is-student {
    --plan-lock-hint-bg: #ff8fa3;
    --plan-lock-hint-fg: #5c0d1c;
}

.plan-lock-content {
    height: 100%;
}

.is-locked .plan-lock-content {
    filter: grayscale(100%);
    opacity: 0.5;
    pointer-events: none;
    user-select: none;
}

.plan-lock-layer {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 1rem;
    text-align: center;
    border-radius: 8px;
    background: rgba(32, 32, 32, 0.6);
    z-index: 2;
}

.plan-lock-icon {
    font-size: 2.6rem;
    color: #ffffff;
    background: #242424;
    border-radius: 50%;
    width: 5rem;
    height: 5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.plan-lock-message {
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.35;
    color: #ffffff;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
}

.plan-lock-hint {
    display: inline-block;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--plan-lock-hint-fg);
    background: var(--plan-lock-hint-bg);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
</style>
